<template>
	<div class="deposit-home" :class="{ 'is-mobile': !isPc }">
		<div class="deposit-page" ref="scrollRoot" @scroll.passive="onScroll">
			<!-- 文案 -->
			<header class="deposit-hero">
				<h1 class="deposit-title">{{ $t('home.depositTitle') }}</h1>
				<p class="deposit-subtitle">{{ $t('home.depositSubtitle') }}</p>
			</header>

			<section v-if="announcement" class="announcement-section">
				<button class="announcement-ticker" type="button" @click="openAnnouncement">
					<span class="announcement-icon" aria-hidden="true">
						<svg viewBox="0 0 24 24" width="18" height="18">
							<path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="M4 13V9a2 2 0 0 1 2-2h2l8-3v14l-8-3H6a2 2 0 0 1-2-2Zm4 2v4m8-9a3 3 0 0 1 0 4"/>
						</svg>
					</span>
					<strong class="announcement-label">{{ $t('home.announcementLabel') }}</strong>
					<span class="announcement-track">
						<span class="announcement-marquee">
							<span>{{ announcementTickerText }}</span>
							<span aria-hidden="true">{{ announcementTickerText }}</span>
						</span>
					</span>
					<span class="announcement-action">{{ $t('home.announcementView') }}</span>
					<svg class="announcement-chevron" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
						<path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="m9 18 6-6-6-6"/>
					</svg>
				</button>
			</section>

			<!-- 总借贷池 -->
			<section class="total-pool-section" v-if="totalPoolItem">
				<div
					class="pool-card total-pool-card"
					role="button"
					tabindex="0"
					:aria-label="displayName(totalPoolItem)"
					@click="onPoolClick(totalPoolItem)"
					@keydown.enter.prevent="onPoolClick(totalPoolItem)"
					@keydown.space.prevent="onPoolClick(totalPoolItem)"
				>
					<div class="pool-card-head total-pool-head">
						<div class="asset-meta">
							<PoolAssetIcon :symbol="totalPoolItem.symbol" :logo-url="resolvePoolLogoUrl(totalPoolItem)" />
							<div class="asset-text">
								<div class="asset-name">{{ displayName(totalPoolItem) }}</div>
								<div class="asset-sub">{{ displaySub(totalPoolItem) }}</div>
							</div>
						</div>
						<span class="total-pool-open" aria-hidden="true">
							<svg viewBox="0 0 24 24" width="18" height="18">
								<path fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" d="M7 17L17 7M9 7h8v8" />
							</svg>
						</span>
					</div>
					<div class="pool-card-rows">
						<div class="stat-row stat-row-money">
							<span class="stat-label">{{ $t('lendingDetail.totalDeposits') }}</span>
							<div class="stat-val">
								<div class="stat-primary">{{ formatUsd(totalSupplyUsd(totalPoolItem)) }}</div>
							</div>
						</div>
						<div class="stat-row stat-row-rate">
							<span class="stat-label">{{ $t('lendingDetail.depositApy') }}</span>
							<div class="stat-val stat-val-inline">
								<span class="apy-badge" :style="{ background: chartColor(0) }">
									<svg viewBox="0 0 16 16" width="10" height="10" aria-hidden="true">
										<path fill="none" stroke="#fff" stroke-width="1.4" stroke-linecap="round" d="M2 10c2-4 4-4 6 0s4 4 6 0"/>
									</svg>
								</span>
								<span class="stat-primary">{{ formatApy(totalPoolItem.apy) }}</span>
							</div>
						</div>
						<div class="stat-row stat-row-money">
							<span class="stat-label">{{ $t('lendingDetail.totalBorrows') }}</span>
							<div class="stat-val">
								<div class="stat-primary">{{ formatUsd(totalPoolItem.totalBorrowUsd) }}</div>
							</div>
						</div>
						<div class="stat-row stat-row-rate">
							<span class="stat-label">{{ $t('lendingDetail.borrowApy') }}</span>
							<div class="stat-val">
								<div class="stat-primary">{{ formatApy(totalPoolItem.borrowApy) }}</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<!-- Featured 横向 Top3 -->
			<section class="featured-section" v-if="featuredPools.length">
				<div class="featured-header">
					<span class="featured-title">{{ $t('home.featured') }}</span>
					<div class="featured-nav" v-if="isPc || featuredPools.length > 1">
						<div class="nav-btn" :class="{ disabled: featuredIndex <= 0 }" @click="featuredIndex > 0 && scrollFeatured(-1)">
							<svg viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
						</div>
						<div class="nav-btn" :class="{ disabled: featuredIndex >= featuredPools.length - 1 }" @click="featuredIndex < featuredPools.length - 1 && scrollFeatured(1)">
							<svg viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
						</div>
					</div>
				</div>
				<div class="featured-track" ref="featuredTrack" @scroll.passive="onFeaturedScroll">
					<div
						class="featured-card"
						v-for="(item, index) in featuredPools"
						:key="'f-' + item.id"
						@click="onPoolClick(item)"
					>
						<div class="featured-card-top">
							<div class="asset-meta">
								<PoolAssetIcon :symbol="item.symbol" :logo-url="resolvePoolLogoUrl(item)" />
								<div class="asset-text">
									<div class="asset-name">{{ displayName(item) }}</div>
									<div class="asset-sub">{{ item.symbol }} · {{ item.chain || '-' }}</div>
								</div>
							</div>
							<div class="featured-apy">
								<span>{{ formatApy(item.apy) }}</span>
								<span class="apy-unit">APY</span>
								<span class="apy-badge" :style="{ background: chartColor(index) }"></span>
							</div>
						</div>
						<div class="featured-chart" :ref="'featuredChart' + item.id"></div>
					</div>
				</div>
			</section>

			<!-- 筛选 -->
			<section class="filter-section">
				<div class="filter-row">
					<div class="search-box">
						<svg class="search-icon" viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
						<input
							v-model="keyword"
							type="text"
							:placeholder="$t('home.filterAssets')"
							@input="onSearchInput"
						/>
					</div>
					<div class="view-toggle" v-if="isPc">
						<div class="view-btn" :class="{ active: viewMode === 'list' }" @click="viewMode = 'list'">
							<svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/></svg>
						</div>
						<div class="view-btn" :class="{ active: viewMode === 'card' }" @click="viewMode = 'card'">
							<svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M4 11h5V5H4v6zm0 7h5v-6H4v6zm6 0h5v-6h-5v6zm6 0h5v-6h-5v6zm-6-7h5V5h-5v6zm6-6v6h5V5h-5z"/></svg>
						</div>
					</div>
				</div>
				<div class="filter-chips">
					<div class="chip-select" :class="{ open: chainMenuOpen }">
						<div class="chip" @click="chainMenuOpen = !chainMenuOpen">
							<span>{{ selectedChain || $t('home.allChains') }}</span>
							<svg viewBox="0 0 24 24" width="14" height="14"><path fill="currentColor" d="M7 10l5 5 5-5z"/></svg>
						</div>
						<div class="chip-menu" v-if="chainMenuOpen">
							<div class="chip-option" @click="selectChain('')">{{ $t('home.allChains') }}</div>
							<div class="chip-option" v-for="c in chainOptions" :key="c" @click="selectChain(c)">{{ c }}</div>
						</div>
					</div>
					<div class="chip sort-chip" @click="toggleSort">
						<span>{{ sortLabel }}</span>
						<svg viewBox="0 0 24 24" width="14" height="14"><path fill="currentColor" d="M7 10l5 5 5-5z"/></svg>
					</div>
				</div>
			</section>

			<!-- PC 表格 -->
			<section class="pool-table" v-if="isPc && viewMode === 'list'">
				<div class="table-head">
					<div class="col col-asset">{{ $t('home.colAsset') }}</div>
					<div class="col col-apy">{{ $t('home.colApy') }}</div>
					<div class="col col-tvl sortable" @click="setSort('tvl')">
						{{ $t('home.colTotalDeposits') }}
						<span class="sort-mark" v-if="sort === 'tvl'">↓</span>
					</div>
					<div class="col col-chain">{{ $t('home.colChain') }}</div>
				</div>
				<div
					class="table-row"
					v-for="item in displayPoolList"
					:key="item.isTotalPool ? 'total' : item.id"
					@click="onPoolClick(item)"
				>
					<div class="col col-asset">
						<PoolAssetIcon :symbol="item.symbol" :logo-url="resolvePoolLogoUrl(item)" size="sm" />
						<div>
							<div class="row-name">{{ displayName(item) }}</div>
							<div class="row-sub">{{ displaySub(item) }}</div>
						</div>
					</div>
					<div class="col col-apy">{{ formatApy(item.apy) }}</div>
					<div class="col col-tvl">
						<div class="row-name">{{ formatUsd(item.tvlUsd) }}</div>
						<div class="row-sub">USD</div>
					</div>
					<div class="col col-chain">{{ item.chain || '-' }}</div>
				</div>
			</section>

			<!-- 移动端 / 卡片视图 -->
			<section class="pool-cards" v-if="!isPc || viewMode === 'card'">
				<div
					class="pool-card"
					v-for="(item, index) in displayPoolList"
					:key="item.isTotalPool ? 'c-total' : ('c-' + item.id)"
					@click="onPoolClick(item)"
				>
					<div class="pool-card-head">
						<div class="asset-meta">
							<PoolAssetIcon :symbol="item.symbol" :logo-url="resolvePoolLogoUrl(item)" />
							<div class="asset-text">
								<div class="asset-name">{{ displayName(item) }}</div>
								<div class="asset-sub">{{ displaySub(item) }}</div>
							</div>
						</div>
					</div>
					<div class="pool-card-divider"></div>
					<div class="pool-card-rows">
						<div class="stat-row">
							<span class="stat-label">{{ $t('lendingDetail.totalDeposits') }}</span>
							<div class="stat-val">
								<div class="stat-primary">{{ formatTokenAmount(item, totalSupplyUsd(item)) }} {{ item.symbol }}</div>
								<div class="stat-secondary">{{ formatUsd(totalSupplyUsd(item)) }}</div>
							</div>
						</div>
						<div class="stat-row">
							<span class="stat-label">{{ $t('lendingDetail.depositApy') }}</span>
							<div class="stat-val stat-val-inline">
								<span class="apy-badge" :style="{ background: chartColor(index) }">
									<svg viewBox="0 0 16 16" width="10" height="10" aria-hidden="true">
										<path fill="none" stroke="#fff" stroke-width="1.4" stroke-linecap="round" d="M2 10c2-4 4-4 6 0s4 4 6 0"/>
									</svg>
								</span>
								<span class="stat-primary">{{ formatApy(item.apy) }}</span>
							</div>
						</div>
						<div class="stat-row">
							<span class="stat-label">{{ $t('lendingDetail.totalBorrows') }}</span>
							<div class="stat-val">
								<div class="stat-primary">{{ formatTokenAmount(item, item.totalBorrowUsd) }} {{ item.symbol }}</div>
								<div class="stat-secondary">{{ formatUsd(item.totalBorrowUsd) }}</div>
							</div>
						</div>
						<div class="stat-row">
							<span class="stat-label">{{ $t('lendingDetail.borrowApy') }}</span>
							<div class="stat-val">
								<div class="stat-primary">{{ formatApy(item.borrowApy) }}</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<div class="list-status">
				<div v-if="loading" class="status-text">{{ $t('home.loading') }}</div>
				<div v-else-if="!displayPoolList.length" class="status-text">{{ $t('home.noPools') }}</div>
				<div v-else-if="!hasMore" class="status-text">{{ $t('home.noMore') }}</div>
				<div ref="loadSentinel" class="load-sentinel"></div>
			</div>
		</div>

		<transition name="announcement-fade">
			<div v-if="announcementOpen && announcement" class="announcement-backdrop" @click.self="closeAnnouncement">
				<article
					class="announcement-dialog"
					role="dialog"
					aria-modal="true"
					aria-labelledby="announcement-dialog-title"
				>
					<header>
						<span>{{ $t('home.announcementLabel') }}</span>
						<button ref="announcementClose" type="button" :aria-label="$t('home.announcementClose')" @click="closeAnnouncement">
							<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
								<path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" d="m6 6 12 12M18 6 6 18"/>
							</svg>
						</button>
					</header>
					<h2 id="announcement-dialog-title">{{ announcement.title }}</h2>
					<div class="announcement-dialog-content">{{ announcement.content }}</div>
				</article>
			</div>
		</transition>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { getAnnouncement, listLendingPools, getLendingPool, getTotalLendingPool } from '@/api/market.js'
import PoolAssetIcon from '@/components/PoolAssetIcon.vue'
import {
	getAnnouncementTickerText,
	normalizeAnnouncementLanguage,
	resolveAnnouncement
} from '@/utils/announcement.js'
import { getTotalPoolSecondaryText, isTotalPoolVisible } from '@/utils/totalPoolConfig.js'

const CHART_COLORS = ['#22c55e', '#3b82f6', '#a855f7', '#f59e0b', '#14b8a6']

export default {
	name: 'HomePc',
	components: { PoolAssetIcon },
	data() {
		return {
			announcement: null,
			announcementOpen: false,
			totalPool: null,
			featuredPools: [],
			featuredIndex: 0,
			poolList: [],
			pageNo: 1,
			pageSize: 15,
			total: 0,
			hasMore: true,
			loading: false,
			keyword: '',
			searchTimer: null,
			selectedChain: '',
			chainOptions: [],
			chainMenuOpen: false,
			sort: 'tvl',
			viewMode: 'list',
			chartInstances: {},
			observer: null,
			legacyAllPools: null
		}
	},
	computed: {
		...mapGetters(['getUserInfo']),
		...mapState(['isPc']),
		sortLabel() {
			return this.sort === 'apy'
				? this.$t('home.sortApy')
				: this.$t('home.sortTotalDeposits')
		},
		displayPoolList() {
			return this.poolList
		},
		totalPoolItem() {
			return this.buildTotalPoolListItem(this.totalPool)
		},
		announcementTickerText() {
			return getAnnouncementTickerText(this.announcement)
		}
	},
	watch: {
		'$i18n.locale'() {
			this.loadAnnouncement()
		},
		isPc(val) {
			if (!val) this.viewMode = 'card'
		}
	},
	mounted() {
		if (!this.isPc) this.viewMode = 'card'
		this.loadTotalPool()
		this.loadAnnouncement()
		this.loadFeatured()
		this.loadPools(true)
		this.$nextTick(() => this.setupObserver())
		document.addEventListener('click', this.onDocClick)
		document.addEventListener('keydown', this.onAnnouncementKeydown)
	},
	beforeDestroy() {
		document.removeEventListener('click', this.onDocClick)
		document.removeEventListener('keydown', this.onAnnouncementKeydown)
		if (this.searchTimer) clearTimeout(this.searchTimer)
		if (this.observer) this.observer.disconnect()
		Object.values(this.chartInstances).forEach((c) => {
			try { c.dispose() } catch (e) { /* ignore */ }
		})
	},
	methods: {
		async loadAnnouncement() {
			try {
				const language = normalizeAnnouncementLanguage(this.$i18n && this.$i18n.locale)
				const res = await getAnnouncement(language)
				this.announcement = resolveAnnouncement(res && res.result)
				if (!this.announcement) this.announcementOpen = false
			} catch (e) {
				console.warn('load homepage announcement failed', e)
				this.announcement = null
				this.announcementOpen = false
			}
		},
		openAnnouncement() {
			this.announcementOpen = true
			this.$nextTick(() => {
				if (this.$refs.announcementClose) this.$refs.announcementClose.focus()
			})
		},
		closeAnnouncement() {
			this.announcementOpen = false
		},
		onAnnouncementKeydown(event) {
			if (event.key === 'Escape' && this.announcementOpen) this.closeAnnouncement()
		},
		onDocClick(e) {
			if (!this.$el.contains(e.target)) this.chainMenuOpen = false
			else if (!e.target.closest('.chip-select')) this.chainMenuOpen = false
		},
		displayName(item) {
			// Morpho 风格：主标题用资产全称（如 Global Dollar）
			return item.assetName || this.knownAssetName(item.symbol) || item.symbol || item.protocolName || '-'
		},
		displaySub(item) {
			if (item && item.isTotalPool) {
				return getTotalPoolSecondaryText(
					item,
					periodDays => this.$tc('home.periodDays', periodDays, { count: periodDays })
				) || item.symbol || '-'
			}
			// 副标题优先展示符号（如 USDG）
			return item.symbol || item.protocolName || item.protocol || '-'
		},
		knownAssetName(symbol) {
			const map = { USDG: 'Global Dollar', GHO: 'GHO', USDC: 'USD Coin', USDT: 'Tether', DAI: 'Dai Stablecoin', WETH: 'Wrapped Ether', WBTC: 'Wrapped BTC' }
			return map[(symbol || '').toUpperCase()] || ''
		},
		resolvePoolLogoUrl(item) {
			if (!item) return ''
			return item.logoUrl || item.logo_url || ''
		},
		chartColor(index) {
			return CHART_COLORS[index % CHART_COLORS.length]
		},
		formatApy(apy) {
			if (apy == null || apy === '') return '-'
			const n = Number(apy)
			if (Number.isNaN(n)) return '-'
			return n.toFixed(2) + '%'
		},
		formatUsd(val) {
			if (val == null || val === '') return '-'
			const n = Number(val)
			if (Number.isNaN(n)) return '-'
			const abs = Math.abs(n)
			const lang = (this.$i18n && this.$i18n.locale) || 'en'
			const useCn = lang === 'zh' || lang === 'hk'
			if (useCn) {
				if (abs >= 1e8) return '$' + (n / 1e8).toFixed(2) + '亿'
				if (abs >= 1e4) return '$' + (n / 1e4).toFixed(2) + '万'
			} else {
				if (abs >= 1e9) return '$' + (n / 1e9).toFixed(2) + 'B'
				if (abs >= 1e6) return '$' + (n / 1e6).toFixed(2) + 'M'
			}
			if (abs >= 1e3) return '$' + (n / 1e3).toFixed(2) + 'K'
			return '$' + n.toFixed(2)
		},
		totalSupplyUsd(item) {
			if (!item) return null
			return item.totalSupplyUsd != null ? item.totalSupplyUsd : item.tvlUsd
		},
		formatTokenAmount(item, usdVal) {
			if (usdVal == null || usdVal === '') return '-'
			const usd = Number(usdVal)
			if (Number.isNaN(usd)) return '-'
			const price = Number(item && item.assetPriceUsd)
			const amount = price && price > 0 ? usd / price : usd
			const abs = Math.abs(amount)
			const lang = (this.$i18n && this.$i18n.locale) || 'en'
			const useCn = lang === 'zh' || lang === 'hk'
			if (useCn) {
				if (abs >= 1e8) return (amount / 1e8).toFixed(2) + '亿'
				if (abs >= 1e4) return (amount / 1e4).toFixed(2) + '万'
			} else {
				if (abs >= 1e9) return (amount / 1e9).toFixed(2) + 'B'
				if (abs >= 1e6) return (amount / 1e6).toFixed(2) + 'M'
				if (abs >= 1e3) return (amount / 1e3).toFixed(2) + 'K'
			}
			return amount.toLocaleString(undefined, { maximumFractionDigits: 2 })
		},
		buildTotalPoolListItem(data) {
			if (!isTotalPoolVisible(data)) return null
			const name = data.poolName || '-'
			return {
				id: 'total',
				isTotalPool: true,
				assetName: name,
				subtitle: data.subtitle || '',
				description: data.description || '',
				periodDays: data.periodDays,
				periodLabel: data.periodLabel || '',
				symbol: name,
				logoUrl: data.logoUrl || data.logo_url || '',
				protocolName: name,
				protocol: name,
				chain: '-',
				apy: data.yieldRate,
				borrowApy: data.borrowApy,
				tvlUsd: data.tvlUsd,
				totalSupplyUsd: data.totalSupplyUsd,
				totalBorrowUsd: data.totalBorrowUsd,
				availableLiquidityUsd: data.availableLiquidityUsd,
				supplyCapUsd: data.supplyCapUsd,
				borrowCapUsd: data.borrowCapUsd,
				utilizationRate: data.utilizationRate,
				maxLtv: data.maxLtv,
				assetPriceUsd: 1
			}
		},
		async loadTotalPool() {
			try {
				const res = await getTotalLendingPool()
				this.totalPool = (res && res.result) || null
			} catch (e) {
				console.warn('load total lending pool failed', e)
				this.totalPool = null
			}
		},
		async loadFeatured() {
			try {
				const res = await listLendingPools({ pageNo: 1, pageSize: 3, sort: 'tvl', featured: true })
				const data = res && res.result
				const records = (data && data.records) || (Array.isArray(data) ? data : []) || []
				this.featuredPools = records.slice(0, 3)
				await this.$nextTick()
				for (let i = 0; i < this.featuredPools.length; i++) {
					await this.renderFeaturedChart(this.featuredPools[i], i)
				}
			} catch (e) {
				console.warn('load featured pools failed', e)
			}
		},
		async renderFeaturedChart(pool, index) {
			const refKey = 'featuredChart' + pool.id
			let el = this.$refs[refKey]
			if (Array.isArray(el)) el = el[0]
			if (!el || !this.$echarts) return

			let values = []
			try {
				const res = await getLendingPool(pool.id, { snapshotLimit: 48 })
				const snaps = (res && res.result && res.result.snapshots) || []
				values = snaps
					.slice()
					.reverse()
					.map((s) => Number(s.apy != null ? s.apy : s.tvlUsd))
					.filter((v) => !Number.isNaN(v))
			} catch (e) {
				/* ignore */
			}
			if (values.length < 2) {
				values = this.syntheticSeries(pool)
			}

			const key = pool.id
			if (this.chartInstances[key]) {
				try { this.chartInstances[key].dispose() } catch (e) { /* ignore */ }
			}
			const chart = this.$echarts.init(el)
			this.chartInstances[key] = chart
			const color = this.chartColor(index)
			chart.setOption({
				animation: false,
				grid: { left: 0, right: 0, top: 8, bottom: 0 },
				xAxis: { type: 'category', show: false, data: values.map((_, i) => i) },
				yAxis: {
					type: 'value',
					show: false,
					min: (v) => v.min - (v.max - v.min) * 0.15 || 0,
					max: (v) => v.max + (v.max - v.min) * 0.15 || 1
				},
				series: [{
					type: 'line',
					data: values,
					symbol: 'none',
					smooth: 0.25,
					lineStyle: { width: 1.5, color },
					areaStyle: {
						color: {
							type: 'linear',
							x: 0, y: 0, x2: 0, y2: 1,
							colorStops: [
								{ offset: 0, color: color + '33' },
								{ offset: 1, color: color + '00' }
							]
						}
					}
				}]
			})
		},
		syntheticSeries(pool) {
			const base = Number(pool.apy) || Number(pool.tvlUsd) || 1
			const pct = Number(pool.apyPct7d) || Number(pool.apyPct1d) || 0
			const points = 24
			const arr = []
			for (let i = 0; i < points; i++) {
				const t = i / (points - 1)
				const wave = Math.sin(t * Math.PI * 2.2) * (Math.abs(base) * 0.03)
				const trend = (pct / 100) * base * t
				const spike = i > points - 3 ? Math.abs(base) * 0.08 : 0
				arr.push(Math.max(0, base + wave + trend + spike))
			}
			return arr
		},
		async loadPools(reset) {
			if (this.loading) return
			if (!reset && !this.hasMore) return
			this.loading = true
			if (reset) {
				this.pageNo = 1
				this.hasMore = true
				this.poolList = []
			}
			try {
				let records = []
				let total = 0

				// 旧接口已缓存时，后续页直接本地切片
				if (!reset && this.legacyAllPools) {
					const all = this.filterLegacyPools(this.legacyAllPools)
					total = all.length
					const start = (this.pageNo - 1) * this.pageSize
					records = all.slice(start, start + this.pageSize)
				} else {
					const params = {
						pageNo: this.pageNo,
						pageSize: this.pageSize,
						sort: this.sort
					}
					if (this.keyword.trim()) params.keyword = this.keyword.trim()
					if (this.selectedChain) params.chain = this.selectedChain

					const res = await listLendingPools(params)
					const data = res && res.result
					if (data && Array.isArray(data.records)) {
						this.legacyAllPools = null
						records = data.records
						total = Number(data.total) || 0
						this.collectChains(records)
					} else if (Array.isArray(data)) {
						// 旧接口兼容：本地筛选 + 分页
						this.legacyAllPools = data
						this.collectChains(data)
						const all = this.filterLegacyPools(this.legacyAllPools)
						total = all.length
						const start = (this.pageNo - 1) * this.pageSize
						records = all.slice(start, start + this.pageSize)
					}
				}

				if (reset) this.poolList = records
				else this.poolList = this.poolList.concat(records)

				this.total = total
				this.hasMore = this.poolList.length < total && records.length > 0
				if (records.length) this.pageNo += 1
			} catch (e) {
				console.warn('load pools failed', e)
				this.hasMore = false
			} finally {
				this.loading = false
			}
		},
		filterLegacyPools(list) {
			let all = (list || []).slice()
			const kw = this.keyword.trim().toLowerCase()
			if (kw) {
				all = all.filter((p) => {
					const blob = [p.symbol, p.assetName, p.protocol, p.protocolName, p.chain, p.poolMeta, p.marketName]
						.filter(Boolean).join(' ').toLowerCase()
					return blob.includes(kw)
				})
			}
			if (this.selectedChain) {
				all = all.filter((p) => p.chain === this.selectedChain)
			}
			if (this.sort === 'apy') {
				all.sort((a, b) => (Number(b.apy) || 0) - (Number(a.apy) || 0))
			} else {
				all.sort((a, b) => (Number(b.tvlUsd) || 0) - (Number(a.tvlUsd) || 0))
			}
			return all
		},
		collectChains(list) {
			const set = new Set(this.chainOptions)
			;(list || []).forEach((p) => {
				if (p.chain) set.add(p.chain)
			})
			this.chainOptions = Array.from(set).sort()
		},
		onSearchInput() {
			if (this.searchTimer) clearTimeout(this.searchTimer)
			this.searchTimer = setTimeout(() => this.loadPools(true), 350)
		},
		selectChain(chain) {
			this.selectedChain = chain
			this.chainMenuOpen = false
			this.loadPools(true)
		},
		toggleSort() {
			this.sort = this.sort === 'tvl' ? 'apy' : 'tvl'
			this.loadPools(true)
		},
		setSort(sort) {
			if (this.sort === sort) return
			this.sort = sort
			this.loadPools(true)
		},
		setupObserver() {
			if (!window.IntersectionObserver) return
			if (this.observer) this.observer.disconnect()
			this.observer = new IntersectionObserver((entries) => {
				if (entries.some((e) => e.isIntersecting)) {
					this.loadPools(false)
				}
			}, { root: null, rootMargin: '120px', threshold: 0 })
			const el = this.$refs.loadSentinel
			if (el) this.observer.observe(el)
		},
		onScroll() {
			// 兜底：无 IntersectionObserver 时
			if (window.IntersectionObserver) return
			const root = this.$refs.scrollRoot
			if (!root) return
			if (root.scrollTop + root.clientHeight >= root.scrollHeight - 80) {
				this.loadPools(false)
			}
		},
		scrollFeatured(dir) {
			const track = this.$refs.featuredTrack
			if (!track) return
			const card = track.querySelector('.featured-card')
			const step = card ? card.offsetWidth + 12 : 280
			track.scrollBy({ left: dir * step, behavior: 'smooth' })
			this.featuredIndex = Math.min(
				Math.max(this.featuredIndex + dir, 0),
				this.featuredPools.length - 1
			)
		},
		onFeaturedScroll() {
			const track = this.$refs.featuredTrack
			if (!track) return
			const card = track.querySelector('.featured-card')
			if (!card) return
			const step = card.offsetWidth + 12
			this.featuredIndex = Math.round(track.scrollLeft / step)
		},
		onPoolClick(item) {
			if (!item) return
			if (item.isTotalPool) {
				this.$router.push({
					path: '/lending-pool',
					query: { type: 'total' }
				})
				return
			}
			if (!item.id) return
			this.$router.push({
				path: '/lending-pool',
				query: { id: item.id }
			})
		}
	}
}
</script>

<style scoped lang="scss">
.deposit-home {
	min-height: 100vh;
	max-width: 100%;
	overflow-x: clip;
	background: var(--ld-bg, #ffffff);
	color: var(--ld-text, #111111);
	-webkit-tap-highlight-color: transparent;

	input {
		outline: none;
		-webkit-tap-highlight-color: transparent;

		&:focus,
		&:focus-visible,
		&:active {
			outline: none;
			box-shadow: none;
		}
	}

	/* 避开全局 button:active { background: inherit } 造成的闪框 */
	.nav-btn,
	.view-btn,
	.chip,
	.chip-option,
	.pool-card,
	.table-row,
	.featured-card {
		-webkit-tap-highlight-color: transparent;

		&:active {
			outline: none !important;
			box-shadow: none !important;
			transform: none !important;
		}
	}
}

.deposit-page {
	max-width: 1120px;
	margin: 0 auto;
	min-width: 0;
	/* 为全局固定顶栏留出空间（PC / 移动端统一） */
	padding: calc(0.8rem + env(safe-area-inset-top, 0px)) 24px 80px;
	box-sizing: border-box;
}

.deposit-home.is-mobile .deposit-page {
	padding: 0.8rem 16px calc(24px + env(safe-area-inset-bottom, 0px));
	max-height: none;
}

.deposit-hero {
	margin-bottom: 28px;
}

.deposit-title {
	margin: 0;
	font-size: 40px;
	font-weight: 700;
	letter-spacing: -0.02em;
	line-height: 1.15;
	color: var(--ld-text, #111111);
}

.deposit-subtitle {
	margin: 10px 0 0;
	font-size: 15px;
	line-height: 1.5;
	color: var(--ld-text-secondary, #888888);
	max-width: 520px;
}

.deposit-home.is-mobile .deposit-title {
	font-size: 32px;
}

.announcement-section {
	margin: -8px 0 24px;
}

.announcement-ticker {
	width: 100%;
	min-height: 48px;
	padding: 0 15px;
	display: grid;
	grid-template-columns: auto auto minmax(0, 1fr) auto auto;
	align-items: center;
	gap: 11px;
	border: 1px solid rgba(37, 99, 235, 0.18);
	border-radius: 12px;
	color: var(--ld-text, #111111);
	background:
		linear-gradient(90deg, rgba(6, 182, 212, 0.08), rgba(37, 99, 235, 0.045) 46%, rgba(37, 99, 235, 0.08)),
		var(--ld-bg-card, #ffffff);
	box-shadow: 0 10px 30px rgba(23, 52, 91, 0.06);
	cursor: pointer;
	text-align: left;
	transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

	&:hover,
	&:focus-visible {
		border-color: rgba(37, 99, 235, 0.36);
		box-shadow: 0 14px 34px rgba(23, 52, 91, 0.1);
		transform: translateY(-1px);
	}

	&:focus-visible {
		outline: 3px solid rgba(37, 99, 235, 0.16);
		outline-offset: 2px;
	}
}

.announcement-icon {
	width: 30px;
	height: 30px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border-radius: 9px;
	color: #ffffff;
	background: #2563eb;
	box-shadow: 0 7px 18px rgba(37, 99, 235, 0.24);
}

.announcement-label {
	color: #1d4ed8;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.04em;
}

.announcement-track {
	min-width: 0;
	overflow: hidden;
	-webkit-mask-image: linear-gradient(90deg, transparent, #000 4%, #000 96%, transparent);
	mask-image: linear-gradient(90deg, transparent, #000 4%, #000 96%, transparent);
}

.announcement-marquee {
	width: max-content;
	display: flex;
	align-items: center;
	animation: announcement-scroll 20s linear infinite;

	> span {
		padding-right: 90px;
		font-size: 13px;
		font-weight: 550;
		white-space: nowrap;
	}
}

.announcement-action {
	color: #2563eb;
	font-size: 12px;
	font-weight: 600;
}

.announcement-chevron {
	color: #2563eb;
}

@keyframes announcement-scroll {
	from { transform: translateX(0); }
	to { transform: translateX(-50%); }
}

.announcement-backdrop {
	position: fixed;
	z-index: 2200;
	inset: 0;
	padding: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(15, 23, 42, 0.52);
	backdrop-filter: blur(8px);
}

.announcement-dialog {
	width: min(560px, 100%);
	max-height: min(720px, calc(100vh - 48px));
	overflow: auto;
	padding: 28px;
	border: 1px solid rgba(255, 255, 255, 0.2);
	border-radius: 18px;
	color: var(--ld-text, #111111);
	background: var(--ld-bg-card, #ffffff);
	box-shadow: 0 32px 90px rgba(15, 23, 42, 0.24);

	header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 18px;

		> span {
			padding: 5px 10px;
			border-radius: 999px;
			color: #1d4ed8;
			background: rgba(37, 99, 235, 0.1);
			font-size: 11px;
			font-weight: 700;
			letter-spacing: 0.05em;
		}

		button {
			width: 36px;
			height: 36px;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			border: 1px solid var(--ld-border, #e5e7eb);
			border-radius: 50%;
			color: var(--ld-text-secondary, #6b7280);
			background: var(--ld-bg-card, #ffffff);
			cursor: pointer;
		}
	}

	h2 {
		margin: 24px 0 14px;
		font-size: 26px;
		font-weight: 720;
		line-height: 1.3;
		letter-spacing: -0.025em;
	}
}

.announcement-dialog-content {
	color: var(--ld-text-secondary, #4b5563);
	font-size: 14px;
	line-height: 1.8;
	white-space: pre-wrap;
	word-break: break-word;
}

.announcement-fade-enter-active,
.announcement-fade-leave-active {
	transition: opacity 0.2s ease;

	.announcement-dialog {
		transition: transform 0.2s ease, opacity 0.2s ease;
	}
}

.announcement-fade-enter,
.announcement-fade-leave-to {
	opacity: 0;

	.announcement-dialog {
		opacity: 0;
		transform: translateY(14px) scale(0.98);
	}
}

.deposit-home.is-mobile {
	.announcement-section {
		margin-top: -4px;
	}

	.announcement-ticker {
		min-height: 46px;
		padding: 0 12px;
		grid-template-columns: auto minmax(0, 1fr) auto;
		gap: 9px;
	}

	.announcement-label,
	.announcement-action {
		display: none;
	}

	.announcement-dialog {
		padding: 22px 20px;
		border-radius: 16px;

		h2 {
			font-size: 22px;
		}
	}

}

@media (prefers-reduced-motion: reduce) {
	.announcement-marquee {
		animation: none;
	}

	.announcement-marquee > span[aria-hidden='true'] {
		display: none;
	}
}

.total-pool-section {
	margin-bottom: 28px;
}

.total-pool-card {
	margin-bottom: 0;
	position: relative;
	overflow: hidden;
	padding: 0;
	border-color: rgba(47, 111, 237, 0.2);
	background:
		radial-gradient(circle at 92% 0%, rgba(37, 99, 235, 0.12), transparent 34%),
		var(--ld-bg-card, #ffffff);
	box-shadow: 0 18px 50px rgba(18, 35, 58, 0.08);
	transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 3px;
		background: linear-gradient(90deg, #06b6d4 0%, #2563eb 58%, #8b5cf6 100%);
	}

	&:hover,
	&:focus-visible {
		transform: translateY(-2px);
		border-color: rgba(37, 99, 235, 0.42);
		box-shadow: 0 24px 64px rgba(18, 35, 58, 0.13);
	}

	&:focus-visible {
		outline: 3px solid rgba(37, 99, 235, 0.2);
		outline-offset: 3px;
	}

	.total-pool-head {
		margin: 0;
		padding: 22px 24px 20px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
	}

	.pool-asset-icon {
		width: 52px;
		height: 52px;
		box-shadow: 0 8px 24px rgba(37, 99, 235, 0.14);
	}

	.asset-name {
		font-size: 20px;
		font-weight: 700;
		letter-spacing: -0.02em;
	}

	.asset-sub {
		margin-top: 4px;
		font-size: 13px;
		font-weight: 500;
	}

	.total-pool-open {
		width: 38px;
		height: 38px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex: 0 0 auto;
		border: 1px solid var(--ld-border, #e5e7eb);
		border-radius: 50%;
		color: #2563eb;
		background: var(--ld-bg-card, #ffffff);
		box-shadow: 0 6px 18px rgba(18, 35, 58, 0.07);
	}

	.pool-card-rows {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 0;
		border-top: 1px solid var(--ld-border, #ececec);
	}

	.stat-row {
		min-height: 112px;
		padding: 20px 22px;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		align-items: flex-start;
		gap: 14px;
		border-right: 1px solid var(--ld-border, #ececec);
		font-size: 13px;

		&:last-child {
			border-right: 0;
		}
	}

	.stat-label {
		padding: 0;
		font-weight: 600;
		color: var(--ld-text-secondary, #737b89);
		letter-spacing: 0.01em;
	}

	.stat-val,
	.stat-val-inline {
		width: 100%;
		justify-content: flex-start;
		text-align: left;
	}

	.stat-primary {
		font-size: clamp(20px, 2vw, 28px);
		font-weight: 750;
		line-height: 1.1;
		letter-spacing: -0.035em;
	}

	.stat-row-rate {
		background: rgba(37, 99, 235, 0.035);

		.stat-primary {
			color: #2563eb;
		}

		.apy-badge {
			width: 24px;
			height: 24px;
			box-shadow: 0 5px 14px rgba(34, 197, 94, 0.24);
		}
	}
}

.deposit-home.is-mobile .total-pool-card {
	.total-pool-head {
		padding: 20px 18px 18px;
	}

	.pool-card-rows {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	.stat-row {
		min-height: 100px;
		padding: 17px 16px;
		border-bottom: 1px solid var(--ld-border, #ececec);

		&:nth-child(2n) {
			border-right: 0;
		}

		&:nth-last-child(-n + 2) {
			border-bottom: 0;
		}
	}

	.stat-primary {
		font-size: 20px;
	}
}

.featured-section {
	margin-bottom: 28px;
}

.featured-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 14px;
}

.featured-title {
	font-size: 18px;
	font-weight: 600;
	color: var(--ld-text, #111111);
}

.featured-nav {
	display: flex;
	gap: 8px;
}

.nav-btn {
	width: 32px;
	height: 32px;
	border-radius: 50%;
	border: 1px solid var(--ld-border, #e8e8e8);
	background: var(--ld-bg-muted, #f5f5f5);
	color: var(--ld-text, #333);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	padding: 0;
	box-sizing: border-box;

	&.disabled {
		opacity: 0.35;
		cursor: default;
		pointer-events: none;
	}

	&:not(.disabled):hover {
		background: var(--ld-bg-active, #ebebeb);
	}

	&:active {
		background: var(--ld-bg-muted, #f5f5f5) !important;
		border-color: var(--ld-border, #e8e8e8) !important;
		border-radius: 50% !important;
	}
}

.featured-track {
	display: flex;
	gap: 12px;
	overflow-x: auto;
	scroll-snap-type: x mandatory;
	padding-bottom: 4px;
	-webkit-overflow-scrolling: touch;
	/* 仅在此区域响应横向滑动，竖向分量交给页面滚动 */
	touch-action: pan-x pinch-zoom;
	overscroll-behavior-x: contain;

	&::-webkit-scrollbar {
		display: none;
	}
}

.featured-card {
	flex: 0 0 320px;
	scroll-snap-align: start;
	background: var(--ld-bg-card, #ffffff);
	border-radius: 16px;
	padding: 16px 16px 8px;
	cursor: pointer;
	border: 1px solid var(--ld-border, var(--ld-bg-active, #ebebeb));
	transition: border-color 0.2s, box-shadow 0.2s;

	&:hover {
		border-color: var(--ld-border-strong, #d0d0d0);
		box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
	}
}

.deposit-home.is-mobile .featured-card {
	flex: 0 0 85%;
	min-width: 260px;
}

.featured-card-top {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 12px;
	margin-bottom: 8px;
}

.asset-meta {
	display: flex;
	align-items: center;
	gap: 10px;
	min-width: 0;
}

.asset-name {
	font-size: 15px;
	font-weight: 600;
	color: var(--ld-text, #111111);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.asset-sub {
	font-size: 12px;
	color: var(--ld-text-secondary, #888888);
	margin-top: 2px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.featured-apy {
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 15px;
	font-weight: 600;
	color: var(--ld-text, #111111);
	white-space: nowrap;
}

.apy-unit {
	font-weight: 500;
	color: var(--ld-text-secondary, #666666);
}

.apy-badge {
	width: 18px;
	height: 18px;
	border-radius: 50%;
	opacity: 0.85;
}

.featured-chart {
	width: 100%;
	height: 88px;
}

.filter-section {
	margin-bottom: 18px;
}

.filter-row {
	display: flex;
	gap: 10px;
	align-items: center;
}

.search-box {
	flex: 1;
	display: flex;
	align-items: center;
	gap: 8px;
	background: var(--ld-bg-card, #ffffff);
	border: 1px solid var(--ld-border, #e5e5e5);
	border-radius: 999px;
	padding: 10px 16px;
	color: var(--ld-text-secondary, #888888);

	input {
		flex: 1;
		border: none;
		outline: none;
		background: transparent;
		color: var(--ld-text, #111111);
		font-size: 14px;

		&::placeholder {
			color: var(--ld-text-tertiary, #aaaaaa);
		}
	}
}

.view-toggle {
	display: flex;
	gap: 6px;
}

.view-btn {
	width: 40px;
	height: 40px;
	border-radius: 10px;
	border: 1px solid var(--ld-border, #e5e5e5);
	background: var(--ld-bg-card, #ffffff);
	color: var(--ld-text-secondary, #888888);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	box-sizing: border-box;

	&.active,
	&:hover {
		color: var(--ld-text, #111111);
		border-color: var(--ld-border-strong, #cccccc);
		background: var(--ld-bg-muted, #f7f7f7);
	}

	&:active {
		background: var(--ld-bg-elevated, #ffffff) !important;
		border-color: var(--ld-border, #e5e5e5) !important;
		border-radius: 10px !important;
		color: var(--ld-text-secondary, #888888) !important;
	}

	&.active:active {
		background: var(--ld-bg-muted, #f7f7f7) !important;
		border-color: var(--ld-border-strong, #cccccc) !important;
		color: var(--ld-text, #111111) !important;
	}
}

.filter-chips {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-top: 12px;
}

.chip-select {
	position: relative;
}

.chip {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 8px 14px;
	border-radius: 999px;
	border: 1px solid var(--ld-border, #e5e5e5);
	background: var(--ld-bg-card, #ffffff);
	color: var(--ld-text, #111111);
	font-size: 13px;
	cursor: pointer;
	box-sizing: border-box;

	&:hover {
		border-color: var(--ld-border-strong, #cccccc);
		background: var(--ld-bg-muted, #f7f7f7);
	}

	&:active {
		background: var(--ld-bg-elevated, #ffffff) !important;
		border-color: var(--ld-border, #e5e5e5) !important;
		border-radius: 999px !important;
		color: var(--ld-text, #111111) !important;
	}
}

.chip-menu {
	position: absolute;
	top: calc(100% + 6px);
	left: 0;
	min-width: 160px;
	max-height: 240px;
	overflow-y: auto;
	background: var(--ld-bg-card, #ffffff);
	border: 1px solid var(--ld-border, #e5e5e5);
	border-radius: 12px;
	z-index: 20;
	padding: 6px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.chip-option {
	padding: 8px 10px;
	border-radius: 8px;
	font-size: 13px;
	cursor: pointer;
	color: var(--ld-text, #333333);

	&:hover {
		background: var(--ld-bg-muted, #f5f5f5);
	}
}

.pool-table {
	border: 1px solid var(--ld-border, var(--ld-bg-active, #ebebeb));
	border-radius: 16px;
	overflow: hidden;
	background: var(--ld-bg-card, #ffffff);
}

.table-head,
.table-row {
	display: grid;
	grid-template-columns: 1.4fr 0.7fr 1.2fr 0.8fr;
	gap: 12px;
	align-items: center;
	padding: 14px 20px;
}

.table-head {
	font-size: 12px;
	color: var(--ld-text-secondary, #888888);
	text-transform: uppercase;
	letter-spacing: 0.04em;
	border-bottom: 1px solid var(--ld-border, var(--ld-bg-active, #ebebeb));
	background: var(--ld-bg-muted, var(--ld-bg-muted, #fafafa));
}

.table-row {
	border-bottom: 1px solid var(--ld-border, var(--ld-border, #f0f0f0));
	cursor: pointer;
	transition: background 0.15s;
	color: var(--ld-text, #111111);

	&:last-child {
		border-bottom: none;
	}

	&:hover {
		background: var(--ld-bg-muted, var(--ld-bg-muted, #fafafa));
	}
}

.col-asset {
	display: flex;
	align-items: center;
	gap: 12px;
	min-width: 0;
}

.col-apy {
	font-weight: 600;
}

.sortable {
	cursor: pointer;
	user-select: none;
}

.sort-mark {
	margin-left: 4px;
}

.row-name {
	font-size: 14px;
	font-weight: 500;
	color: var(--ld-text, #111111);
}

.row-sub {
	font-size: 12px;
	color: var(--ld-text-secondary, #888888);
	margin-top: 2px;
}

.pool-cards {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.deposit-home:not(.is-mobile) .pool-cards {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 12px;
}

.pool-card {
	background: var(--ld-bg-card, #ffffff);
	border: 1px solid var(--ld-border, var(--ld-bg-active, #ebebeb));
	border-radius: 16px;
	padding: 16px;
	cursor: pointer;
	transition: border-color 0.2s, box-shadow 0.2s;

	&:hover {
		border-color: var(--ld-border-strong, #d0d0d0);
		box-shadow: var(--ld-shadow, 0 4px 16px rgba(0, 0, 0, 0.06));
	}
}

.pool-card-head {
	margin-bottom: 14px;
}

.pool-card-divider {
	height: 1px;
	background: var(--ld-border, #ececec);
	margin-bottom: 14px;
}

.pool-card-rows {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.stat-row {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 12px;
	font-size: 13px;
}

.stat-label {
	color: var(--ld-text-secondary, #888888);
	flex-shrink: 0;
	padding-top: 1px;
}

.stat-val {
	text-align: right;
	min-width: 0;
}

.stat-val-inline {
	display: inline-flex;
	align-items: center;
	justify-content: flex-end;
	gap: 6px;
}

.stat-primary {
	color: var(--ld-text, #111111);
	font-weight: 600;
	font-size: 14px;
	line-height: 1.35;
}

.stat-secondary {
	font-size: 12px;
	color: var(--ld-text-tertiary, #999999);
	font-weight: 400;
	margin-top: 2px;
	line-height: 1.35;
}

.apy-badge {
	width: 18px;
	height: 18px;
	border-radius: 50%;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.list-status {
	padding: 24px 0 8px;
	text-align: center;
}

.status-text {
	font-size: 13px;
	color: var(--ld-text-tertiary, #999999);
}

.load-sentinel {
	height: 1px;
}
</style>
